/**
 * When to draw the *persisted* markup overlays (saved rulers and point
 * annotations) as the chart's x-window changes.
 *
 * The rule: draw them whenever there is chart data to anchor them to.
 *
 * Both overlays position themselves by projecting a persisted x-domain value
 * (`anchorTimestamp`, `startXValue`/`endXValue`) to a pixel via uPlot's
 * `valToPos` against the CURRENT x-scale. When the user pans or zooms, the
 * previous window's data is deliberately kept on screen while the new window
 * loads (see `shouldPreserveData` in `useTimeSeriesDataProcessing`), and the
 * overlay projects against whatever is currently shown: the preserved window
 * (which the pan tween slides through, the overlay reprojecting each frame) or,
 * for a drag-zoom, the window pinned to the zoomed range for the duration of
 * the load. Either way the marker stays glued to its data position.
 *
 * We used to *defer* drawing until the load settled, to avoid a jump as the
 * overlay reprojected when the new data landed. But deferring blanked saved
 * annotations off the chart for the whole load — dropping content the user was
 * looking at for several seconds, which is worse than the reprojection it
 * avoided (and the pan tween / zoom pin now keep that reprojection smooth). So
 * keep them drawn for as long as there is data on screen; only a genuinely
 * empty chart — a cold load with nothing plotted yet, so nothing to anchor to —
 * leaves them undrawn until its first data arrives.
 *
 * Kept pure and signal-only so the decision is unit-testable without a chart.
 */
export const shouldRenderPersistedOverlays = (params: {
  hasData: boolean
}): boolean => params.hasData
